import type {
  GetOpsPaymentOrderResponse,
  ListOpsPaymentOrdersResponse,
  OpsPaymentOrderStatus,
  ReconcileOpsPaymentOrderRequest,
  ReconcileOpsPaymentOrderResponse,
} from '@nadoutong/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  type InfiniteData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers for ops-api `/v1/ops/payments/*` (Epic #84 P10).
 *
 * Phase-1 contract:
 *   - `listPaymentOrders` returns a cursor-paginated page of
 *     `OpsPaymentOrderSummary` rows. Clients hand the `next_cursor` back
 *     as an opaque string to get the next page — server-side is keyset
 *     paginated on `(created_at DESC, id DESC)`.
 *   - `reconcilePaymentOrder` is idempotent downstream via the
 *     `status='pending'` gate inside `applyTopupCreditBatch`; clients
 *     can click the reconcile button twice without risking double-credit.
 *   - React Query integration uses `useInfiniteQuery` so the "Load more"
 *     button just calls `fetchNextPage()`; each page keeps its own
 *     `next_cursor` in the pageParam chain.
 */

const BASE_PATH = '/v1/ops/payments';

export interface ListOrdersQuery {
  /** CSV list of statuses. Empty / undefined = no filter. */
  readonly status?: readonly OpsPaymentOrderStatus[];
  readonly accountId?: string;
  /** ISO-8601 lower bound (inclusive). */
  readonly from?: string;
  /** ISO-8601 upper bound (exclusive). */
  readonly to?: string;
  readonly limit?: number;
}

function buildListQueryString(
  query: ListOrdersQuery,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  if (query.status !== undefined && query.status.length > 0) {
    params.set('status', query.status.join(','));
  }
  if (typeof query.accountId === 'string' && query.accountId.length > 0) {
    params.set('account_id', query.accountId);
  }
  if (typeof query.from === 'string' && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (typeof query.to === 'string' && query.to.length > 0) {
    params.set('to', query.to);
  }
  if (typeof query.limit === 'number' && Number.isFinite(query.limit)) {
    params.set('limit', String(query.limit));
  }
  if (cursor !== null && cursor.length > 0) {
    params.set('cursor', cursor);
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export async function listPaymentOrders(
  query: ListOrdersQuery,
  cursor: string | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOpsPaymentOrdersResponse> {
  return opsApiRequest<ListOpsPaymentOrdersResponse>(
    `${BASE_PATH}/orders${buildListQueryString(query, cursor)}`,
    { signal },
    fetchImpl,
  );
}

export async function getPaymentOrder(
  orderId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOpsPaymentOrderResponse> {
  return opsApiRequest<GetOpsPaymentOrderResponse>(
    `${BASE_PATH}/orders/${encodeURIComponent(orderId)}`,
    { signal },
    fetchImpl,
  );
}

export async function reconcilePaymentOrder(
  orderId: string,
  body: ReconcileOpsPaymentOrderRequest,
  fetchImpl: typeof fetch,
): Promise<ReconcileOpsPaymentOrderResponse> {
  return opsApiRequest<ReconcileOpsPaymentOrderResponse>(
    `${BASE_PATH}/orders/${encodeURIComponent(orderId)}/reconcile`,
    { method: 'POST', body },
    fetchImpl,
  );
}

// ─── React Query integration ────────────────────────────────────────────

export const PAYMENT_ORDERS_NAMESPACE = ['paymentOrders'] as const;

/**
 * Serialise a `ListOrdersQuery` into a stable tuple for the React Query
 * key. Mutates arrays to sorted copies so `status: ['pending','paid']`
 * and `status: ['paid','pending']` dedupe to the same cache key.
 */
export function paymentOrdersListQueryKey(
  query: ListOrdersQuery,
): readonly [
  'paymentOrders',
  'list',
  {
    status: readonly OpsPaymentOrderStatus[];
    accountId: string | null;
    from: string | null;
    to: string | null;
    limit: number | null;
  },
] {
  const statuses = query.status !== undefined ? [...query.status].sort() : [];
  return [
    'paymentOrders',
    'list',
    {
      status: statuses,
      accountId: query.accountId ?? null,
      from: query.from ?? null,
      to: query.to ?? null,
      limit: query.limit ?? null,
    },
  ] as const;
}

export function useListPaymentOrders(
  query: ListOrdersQuery,
): UseInfiniteQueryResult<InfiniteData<ListOpsPaymentOrdersResponse, string | null>, Error> {
  const { fetchImpl } = useSession();
  return useInfiniteQuery<
    ListOpsPaymentOrdersResponse,
    Error,
    InfiniteData<ListOpsPaymentOrdersResponse, string | null>,
    ReturnType<typeof paymentOrdersListQueryKey>,
    string | null
  >({
    queryKey: paymentOrdersListQueryKey(query),
    queryFn: ({ pageParam, signal }) =>
      listPaymentOrders(query, pageParam ?? null, fetchImpl, signal),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    retry: false,
  });
}

export interface ReconcileVars {
  readonly orderId: string;
  readonly body: ReconcileOpsPaymentOrderRequest;
}

export function useReconcilePaymentOrder(): UseMutationResult<
  ReconcileOpsPaymentOrderResponse,
  Error,
  ReconcileVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, body }) => reconcilePaymentOrder(orderId, body, fetchImpl),
    onSuccess: () => {
      // Invalidate all list queries — reconcile may have flipped an
      // order's status, so any cached page could be stale.
      void qc.invalidateQueries({ queryKey: [...PAYMENT_ORDERS_NAMESPACE, 'list'] });
    },
  });
}
